// Font Imports
import '@fontsource/inter';      // Regular Inter font
import '@fontsource/inter/700.css'; // Bold weight for Inter
import '@fontsource/inter/900.css'; // Extra bold weight for Inter

import './App.css';
import React from 'react';
// Import MuiLink from @mui/material and RouterLink from react-router-dom
import { Box, Typography, Button, createTheme, ThemeProvider, Container, Divider, Link as MuiLink } from '@mui/material';
import { Routes, Route, Link as RouterLink } from 'react-router-dom'; // Renamed react-router-dom Link to RouterLink
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import HowToVoteOutlinedIcon from '@mui/icons-material/HowToVoteOutlined';
import ConnectWithoutContactOutlinedIcon from '@mui/icons-material/ConnectWithoutContactOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import ImportantDevicesIcon from '@mui/icons-material/ImportantDevices';
import SamplePoll from './SamplePoll';
// Replaced PublicIcon with BalanceOutlinedIcon for a favicon-style logo
import BalanceOutlinedIcon from '@mui/icons-material/BalanceOutlined'; // New icon for header
import PlayCircleFilledOutlinedIcon from '@mui/icons-material/PlayCircleFilledOutlined'; // Import for the play button icon
import MailOutlineIcon from '@mui/icons-material/MailOutline'; // Import for the Mail icon
import { motion } from 'framer-motion'; // Import motion for animations

// Define a custom theme with expanded color palette
const theme = createTheme({
  palette: {
    primary: {
      main: '#673AB7', // Deep Purple (Your existing main primary)
      light: '#8A5ACF', // Lighter purple (from your header/footer gradient)
      dark: '#5A32A0',  // A darker shade of purple for deeper accents/hover states
    },
    secondary: {
      main: '#FFFFFF', // White (Your existing secondary)
    },
    info: { // Soft teal accent
      main: '#4DC6B7', // A beautiful, soft teal
      light: '#6DDACF',
      dark: '#009688',
    },
  },
  typography: {
    fontFamily: '"Inter", sans-serif', // Default font for body text set to Inter
    h1: { fontFamily: '"Inter", sans-serif"', fontWeight: 900 }, // Inter, extra bold
    h2: { fontFamily: '"Inter", sans-serif"', fontWeight: 900 }, // Inter, extra bold
    h3: { fontFamily: '"Inter", sans-serif"', fontWeight: 900 }, // Inter, extra bold
    h4: { fontFamily: '"Inter", sans-serif"', fontWeight: 900 }, // Inter, extra bold
    h5: { fontFamily: '"Inter", sans-serif"', fontWeight: 700 }, // Inter, bold (adjusted from 900)
    h6: { fontFamily: '"Inter", sans-serif"', fontWeight: 700 }, // Inter, bold (adjusted from 900)
    body1: {
      lineHeight: 1.7, // Increased line height
      letterSpacing: '0.01em',
      fontWeight: 400, // Explicitly set body font weight
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          marginTop: '20px',
          marginBottom: '20px',
        },
      },
    },
    MuiContainer: {
        styleOverrides: {
          root: {
            marginTop: '20px',
            marginBottom: '20px',
            position: 'relative',
          },
        },
      },
  },
});

function App() {
  const [showVideo, setShowVideo] = React.useState(false); // State to control video display

  return (
    <ThemeProvider theme={theme}>
      <Box className="App" sx={{
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundImage: 'url(\'/images/background.jpg\')',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}>

        {/* Redesigned Header with Gradient, Shadow, and Icon */}
        <Box component="header" sx={{
            backgroundImage: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)', // Gradient background
            color: theme.palette.secondary.main,
            padding: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)', // Drop shadow
            position: 'sticky', // Made header sticky
            top: 0, // Sticks to the top
            zIndex: 1000, // Ensure it stays above other content
          }}>
          {/* Grouping icon and "Ask America" text for alignment */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {/* Updated to BalanceOutlinedIcon and adjusted font size */}
            <BalanceOutlinedIcon sx={{ fontSize: '1.75rem', marginRight: '10px', color: theme.palette.secondary.main }} />
            <Typography
              variant="h4"
              component="h1"
              sx={{
                margin: 0,
                fontWeight: 900,
              }}
            >
              Ask America
            </Typography>
          </Box>

          <Box component="nav" sx={{
            display: 'flex',
            alignItems: 'center',
            marginLeft: "auto",
            gap: '15px', // Space between nav items
           }}>
            {/* Using MuiLink with component={RouterLink} for full MUI sx prop support */}
            <MuiLink component={RouterLink} to="/"
              sx={{
              color: theme.palette.secondary.main,
              textDecoration: 'none',
              fontSize: '1.0rem', // Slightly smaller font for pill style
              padding: '8px 18px', // Increased horizontal padding for pill shape
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              border: `2px solid ${theme.palette.secondary.main}`, // Outlined border
              borderRadius: '9999px', // Pill shape
              transition: 'background-color 0.3s ease, color 0.3s ease, transform 0.2s ease-in-out', // Added transform to transition
              '&:hover': { // Hover effect
                backgroundColor: theme.palette.secondary.main, // Background tint on hover
                color: theme.palette.primary.main, // Change text color on hover
                transform: 'scale(1.05)', // Subtle scaling animation
              },
            }}>
              <HomeOutlinedIcon sx={{ fontSize: '1.2em', marginRight: '5px' }} />
              Home
            </MuiLink>
            {/* Using MuiLink with component={RouterLink} for full MUI sx prop support */}
            <MuiLink component={RouterLink} to="/sample-poll"
              sx={{
              color: theme.palette.secondary.main,
              textDecoration: 'none',
              fontSize: '1.0rem', // Slightly smaller font for pill style
              padding: '8px 18px', // Increased horizontal padding for pill shape
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              border: `2px solid ${theme.palette.secondary.main}`, // Outlined border
              borderRadius: '9999px', // Pill shape
              transition: 'background-color 0.3s ease, color 0.3s ease, transform 0.2s ease-in-out', // Added transform to transition
              '&:hover': { // Hover effect
                backgroundColor: theme.palette.secondary.main, // Background tint on hover
                color: theme.palette.primary.main, // Change text color on hover
                transform: 'scale(1.05)', // Subtle scaling animation
              },
            }}>
              <HowToVoteOutlinedIcon sx={{ fontSize: '1.2em', marginRight: '5px' }} />
              Sample Poll
            </MuiLink>
          </Box>
        </Box>

        <Container component="main" sx={{ flexGrow: 1 }}>
          <Routes>
            <Route path="/" element={
              <Box className="hero-section" sx={{
                marginY: '40px',
                padding: '40px',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
              }}>
                {/* No motion.div on the main hero-content Box, as requested */}
                <Box className="hero-content">
                  {/* Hero Section with integrated Logo and Text */}
                  <Box sx={{
                    backgroundImage: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
                    color: 'white',
                    textAlign: 'center', // Centers the content within this box
                    paddingY: '20px', // Further adjusted padding to make the box smaller
                    paddingX: '16px',
                    borderRadius: '12px', // rounded-xl
                    boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)', // shadow-lg
                    marginBottom: '30px', // Space below this hero section
                  }}>
                    {/* Inner flex box to align logo and text horizontally, centered */}
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center', // Centers the logo+text block horizontally
                      flexWrap: 'wrap', // Allows wrapping on small screens
                      maxWidth: 'fit-content',
                      margin: '0 auto',
                    }}>
                      <img
                        src="/images/logoStatue.png" // Assumes logo is in public/images folder
                        alt="Ask America Logo"
                        style={{
                          height: '180px',     // Increased height for better visibility
                          width: 'auto',       // Maintain aspect ratio
                          marginRight: '25px', // Increased space between logo and text
                          flexShrink: 0,       // Prevent logo from shrinking
                          filter: 'drop-shadow(0 0 12px rgba(255, 255, 255, 1))', // White drop shadow, increased blur and opacity
                        }}
                      />
                      <Box sx={{ textAlign: 'left' }}> {/* Ensures text aligns left within this sub-box */}
                        <Typography variant="h2" component="h1" sx={{
                          fontSize: { xs: '3.5rem', sm: '4.5rem', md: '5rem' }, // Significantly increased font size for "Ask America"
                          fontWeight: 900, // font-bold
                          marginBottom: '10px', // Adjusted margin
                          fontFamily: '"Inter", sans-serif"', // Ensure Inter font
                          textShadow: '3px 3px 6px rgba(0,0,0,0.5)', // Stronger shadow for hero text
                        }}>
                          Ask America
                        </Typography>
                        <Typography variant="h5" component="p" sx={{
                          fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' }, // Significantly increased font size for "Government That Listens"
                          fontFamily: '"Inter", sans-serif"', // Ensure Inter font
                        }}>
                          Government That Listens
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Video Section Modernization */}
                  {/* New headline for the video section */}
                  <Typography variant="h5" component="h3" sx={{
                    marginBottom: '10px',
                    color: theme.palette.primary.dark,
                    fontWeight: 700,
                  }}>
                    Watch our explainer video
                  </Typography>
                  <Typography variant="body2" sx={{
                    marginBottom: '20px',
                    color: theme.palette.grey[700],
                    fontSize: '0.95rem',
                  }}>
                    Watch this short video to understand the core principles and how Ask America aims to make your voice heard.
                  </Typography>

                  <Box sx={{
                    position: 'relative',
                    paddingBottom: '56.25%', // Maintain 16:9 aspect ratio
                    height: 0,
                    overflow: 'hidden',
                    maxWidth: '100%',
                    backgroundColor: '#000',
                    margin: '0 auto 30px auto',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)', // Card shadow
                    borderRadius: '12px', // Card border-radius
                    background: 'white', // Card background
                    display: 'flex', // For centering overlay content
                    justifyContent: 'center', // For centering overlay content
                    alignItems: 'center', // For centering overlay content
                  }}>
                    {!showVideo && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          cursor: 'pointer',
                          backgroundColor: 'rgba(0,0,0,0.7)', // Dark overlay on thumbnail
                          zIndex: 1, // Ensure it's above the iframe (when iframe is hidden)
                        }}
                        onClick={() => setShowVideo(true)}
                      >
                        <img
                          src="https://placehold.co/1280x720/000000/FFFFFF?text=Video+Thumbnail" // Placeholder thumbnail
                          alt="Video Thumbnail"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            opacity: 0.6, // Blend thumbnail with overlay
                            borderRadius: '12px', // Rounded corners for thumbnail
                            boxShadow: '0 4px 8px rgba(0,0,0,0.1)', // Drop shadow for thumbnail
                          }}
                        />
                        <PlayCircleFilledOutlinedIcon sx={{
                          fontSize: '6rem', // Large play button
                          color: 'white',
                          zIndex: 2, // Ensure play button is above thumbnail
                          transition: 'transform 0.2s ease-in-out',
                          '&:hover': {
                            transform: 'scale(1.1)',
                          }
                        }} />
                      </Box>
                    )}
                    {showVideo && (
                      <iframe
                        src="https://www.youtube.com/embed/k3Xx7xhTE5c?autoplay=1&si=SBXVmupp0E0JWGTl" // Added autoplay
                        title="YouTube video player"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                        }}
                      ></iframe>
                    )}
                  </Box>

                  <Divider sx={{
                    borderColor: theme.palette.grey[500],
                    my: 4,
                  }} />

                  <Box className="explanation-text" sx={{
                    marginTop: '0px',
                    textAlign: 'left', // Ensure text aligns left within this section
                  }}>
                    {/* The logo is no longer floated here */}
                    <Typography variant="h5" component="h2" sx={{ marginBottom: '10px' }}>
                      What is Ask America?
                    </Typography>
                    <Typography variant="body1">
                      Ask America is a brand-new website I'm developing, aiming to update how our government works. Imagine this: You, the voter, get to have a direct say on how your House Representative votes on important issues. We put forward major issues, and our big idea is that your representative will vote the way most people in their district want them to.
                      <br /><br />
                      My background as a college professor has prepared me to break down complex topics into simple terms. On Ask America, I'll personally explain each topic, showing what happens if we vote 'yes' or 'no,' and share my own informed thoughts to help you decide.
                      <br /><br />
                      Right now, I'm exploring the idea of running for the House of Representatives in Florida's 11th District, and this website is part of understanding the interest in this new approach to civic engagement.
                      <Typography component="span" variant="body1" sx={{ fontStyle: 'italic', fontWeight: 600 }}>
                        My goal is to make sure your voice truly matters again!
                      </Typography>
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: '20px', marginTop: '30px', marginBottom: '30px' }}>
                    {/* Updated Join Our Email List Button with icon, shadow, and hover */}
                    <Button
                      variant="contained"
                      size="large"
                      sx={{
                        background: theme.palette.primary.main, // Solid background
                        borderRadius: '8px',
                        color: 'white',
                        padding: '12px 24px',
                        fontWeight: 'bold',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.2)', // Added shadow
                        transition: 'all 0.3s ease-in-out', // Added transition
                        '&:hover': {
                          background: theme.palette.primary.dark, // Darken on hover
                          opacity: 1,
                          transform: 'scale(1.02)', // Hover animation
                        },
                      }}
                    >
                      <MailOutlineIcon sx={{ fontSize: '1.2em', marginRight: '8px' }} /> {/* Mail icon */}
                      Join Our Email List
                    </Button>
                    {/* Updated Try A Sample Poll Button with icon, shadow, and hover */}
                    <Button
                      variant="contained"
                      size="large"
                      component={RouterLink} // Use RouterLink here
                      to="/sample-poll"
                      sx={{
                        background: theme.palette.primary.main, // Solid background
                        borderRadius: '8px',
                        color: 'white',
                        padding: '12px 24px',
                        fontWeight: 'bold',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.2)', // Added shadow
                        transition: 'all 0.3s ease-in-out', // Added transition
                        '&:hover': {
                          background: theme.palette.primary.dark, // Darken on hover
                          opacity: 1,
                          transform: 'scale(1.02)', // Hover animation
                        },
                      }}
                    >
                      <HowToVoteOutlinedIcon sx={{ fontSize: '1.2em', marginRight: '8px' }} /> {/* Poll icon */}
                      Try A Sample Poll
                    </Button>
                  </Box>
                </Box>
                {/* </motion.div> - Removed closing tag from here as the wrapper is gone */}

                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center', // Changed to center for horizontal alignment
                  marginTop: '60px',
                  flexWrap: 'wrap',
                  gap: '20px', // Added gap for spacing between cards
                }}>
                  {/* Column 1: Directly Influence Your Rep - Now a Card with motion */}
                  <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.5 }}
                    transition={{ duration: 0.5 }}
                  >
                    <Box sx={{
                      flex: '1 1 300px', // Responsive flex properties for consistent sizing
                      minHeight: '350px', // Set a consistent height for all cards
                      padding: '25px', // Increased padding for card content
                      textAlign: 'center',
                      backgroundColor: 'white', // Card background
                      borderRadius: '12px', // Rounded corners for cards
                      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)', // Subtle shadow for cards
                      borderLeft: `4px solid ${theme.palette.info.main}`, // Colored border matching icon
                      transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out', // Smooth transition on hover
                      '&:hover': {
                        transform: 'translateY(-4px)', // Lift effect on hover (adjusted to -4px)
                        boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)', // More pronounced shadow on hover
                      },
                    }}>
                      <Typography variant="h6" component="h3" sx={{ marginBottom: '15px', color: theme.palette.primary.dark }}>
                        Your Voice, Direct to Your Rep.
                      </Typography>
                      <Box sx={{
                        marginBottom: '15px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '100px',
                      }}>
                        <ConnectWithoutContactOutlinedIcon sx={{ fontSize: '5rem', color: theme.palette.info.main }} />
                      </Box>
                      <Typography variant="body1">
                        Ask America makes it possible for you to directly influence how your House Representative votes. This new approach ensures your opinion is clearly heard, helping them align their vote with the majority of their district. It's about truly making your voice count on the issues that matter.
                      </Typography>
                    </Box>
                  </motion.div>

                  {/* Column 2: Clear Explanations, Every Time - Now a Card with motion */}
                  <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.5 }}
                    transition={{ duration: 0.5, delay: 0.1 }} // Added slight delay
                  >
                    <Box sx={{
                      flex: '1 1 300px', // Responsive flex properties for consistent sizing
                      minHeight: '350px', // Set a consistent height for all cards
                      padding: '25px',
                      textAlign: 'center',
                      backgroundColor: 'white',
                      borderRadius: '12px',
                      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                      borderLeft: `4px solid ${theme.palette.info.main}`, // Colored border matching icon
                      transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
                      },
                    }}>
                      <Typography variant="h6" component="h3" sx={{ marginBottom: '15px', color: theme.palette.primary.dark }}>
                        Understand Every Issue Clearly.
                      </Typography>
                      <Box sx={{
                        marginBottom: '15px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '100px',
                      }}>
                        <LightbulbOutlinedIcon sx={{ fontSize: '5rem', color: theme.palette.info.main }} />
                      </Box>
                      <Typography variant="body1">
                        Government topics can be confusing, but they don't have to be. As a professor, my job is to break down each issue simply. I'll explain what a "yes" vote means, what a "no" vote means, and offer my informed thoughts. You'll have all the facts you need to make your choice with confidence.
                      </Typography>
                    </Box>
                  </motion.div>

                  {/* Column 3: Modernizing How Government Works - Now a Card with motion */}
                  <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.5 }}
                    transition={{ duration: 0.5, delay: 0.2 }} // Added slight delay
                  >
                    <Box sx={{
                      flex: '1 1 300px', // Responsive flex properties for consistent sizing
                      minHeight: '350px', // Set a consistent height for all cards
                      padding: '25px',
                      textAlign: 'center',
                      backgroundColor: 'white',
                      borderRadius: '12px',
                      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                      borderLeft: `4px solid ${theme.palette.info.main}`, // Colored border matching icon
                      transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
                      },
                    }}>
                      <Typography variant="h6" component="h3" sx={{ marginBottom: '15px', color: theme.palette.primary.dark }}>
                        Update Government for Today.
                      </Typography>
                      <Box sx={{
                        marginBottom: '15px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '100px',
                      }}>
                        <ImportantDevicesIcon sx={{ fontSize: '5rem', color: theme.palette.info.main }} />
                      </Box>
                      <Typography variant="body1">
                        Our goal is to bring government into the 21st century. Ask America uses technology to create a direct, two-way conversation between you and your elected officials. It's about bringing transparency, participation, and real accountability back, ensuring our representatives truly listen and act on behalf of the people they serve.
                      </Typography>
                    </Box>
                  </motion.div>
                </Box>
              </Box>
            } />

            <Route path="/sample-poll" element={<SamplePoll />} />
          </Routes>
        </Container>

        <Box component="footer" sx={{
            backgroundImage: 'linear-gradient(to top, #4A148C, #2C0B5E)', // Darker gradient
            color: theme.palette.secondary.main, // White text
            padding: '20px',
            marginTop: '40px'
          }}>
          <Typography variant="body1">
            © 2025 Ask America
          </Typography>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default App;
